import { Mark, mergeAttributes } from '@tiptap/core';
import { swallowAnchorClicks } from './editor_anchor_clicks';

// A nullable string attr kept in a data-* attribute, e.g. ('defaultText',
// 'data-default-text').
function dataAttribute(key: string, attribute: string) {
   return {
      default: null,
      parseHTML: (element: HTMLElement) => element.dataset[key] ?? null,
      renderHTML: (attributes: Record<string, any>) =>
         attributes[key] === null ? {} : { [attribute]: attributes[key] },
   };
}

/**
 * A link to one of our own guides, products, Answers posts or documents: the
 * [guide|...], [product|...], [post|...] and [document|...] tokens, emitted by
 * the server's Json renders (Objects/Text/Wiki/Render/Json/Ifixit/{Guidelink,
 * Productlink,Postlink,Document}.php) and re-emitted as wikitext by
 * to_wiki.ts. The marked text is
 * the link's display text.
 *
 * A token without custom text shows a name looked up when the page renders
 * (the guide's title, say), which the server sends as defaultText. While the
 * text still reads defaultText the token saves without text, so the link
 * keeps following that name; any other text saves as the link's own.
 */
export const SiteLink = Mark.create({
   name: 'siteLink',
   inclusive: false,

   addAttributes() {
      return {
         // The token's tag: guide, product, post or document.
         kind: dataAttribute('kind', 'data-kind'),
         // The guideid, itemcode, postid or documentid.
         id: dataAttribute('id', 'data-id'),
         // The token's other named arguments, as wikitext (|stepid=12).
         options: {
            default: '',
            parseHTML: (element: HTMLElement) => element.dataset.options ?? '',
            renderHTML: attributes =>
               attributes.options ? { 'data-options': attributes.options } : {},
         },
         defaultText: dataAttribute('defaultText', 'data-default-text'),
         newWindow: {
            default: false,
            parseHTML: (element: HTMLElement) => element.dataset.newWindow === 'true',
            renderHTML: attributes => (attributes.newWindow ? { 'data-new-window': 'true' } : {}),
         },
         // Where the link goes, for display only: the token carries the id.
         href: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('href'),
            renderHTML: attributes => (attributes.href === null ? {} : { href: attributes.href }),
         },
      };
   },

   parseHTML() {
      return [{ tag: 'a[data-site-link]' }];
   },

   renderHTML({ HTMLAttributes }) {
      return ['a', mergeAttributes({ 'data-site-link': '' }, HTMLAttributes), 0];
   },

   addProseMirrorPlugins() {
      return [swallowAnchorClicks('siteLinkClick', 'a[data-site-link]')];
   },
});
